"use client";
import { useIsMobile } from "@/utils/useIsMobile";
import useScrollDirection from "@/utils/useScrollDirection";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useRef } from "react";

const hrefs = [
  "overview",
  "returns",
  "risk",
  "portfolio-details",
  "investment-details",
];
const labels = ["Overview", "Returns", "Risk", "Portfolio", "Information"];

export const SchemeFundTabs = ({
  slug,
  id,
}: {
  slug: string;
  id?: number | "";
}) => {
  const pathname = usePathname();

  const segments = pathname.split("/");
  const currentTab = segments[3] || "overview";

  const tabRefs = useRef<(HTMLDivElement | null)[]>([]);

  const scrollDirection = useScrollDirection(20);
  const showNav = scrollDirection !== "down";
  const isMobile = useIsMobile(991);

  useEffect(() => {
    const index = hrefs.indexOf(currentTab);
    const activeRef = tabRefs.current[index];
    if (activeRef) {
      activeRef.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
    }
  }, [currentTab]);

  return (
    <section
      className={[
        "section px-2.5 sm:px-5 2xl:px-0 mb-5 sm:mb-9 sticky z-50 transition duration-400",
        isMobile ? (showNav ? "top-20.5 sm:top-22.5" : "top-3.5") : "top-22.5",
      ].join(" ")}
    >
      <div className="flex items-center justify-between gap-3 xs:gap-7 p-1 overflow-x-auto scrollbar-none rounded-full border border-[#1A51AA24] shadow-[0px_3px_5px_1px_#0000000D] overflow-hidden bg-[#EBF3FF]">
        {labels.map((label, i) => {
          const segment = hrefs[i];
          const isActive =
            currentTab === segment ||
            (segment === "overview" && !hrefs.includes(currentTab));
          const href =
            segment === "overview"
              ? `/mutual-fund/${slug}/mso${id}`
              : `/mutual-fund/${slug}/${segment}/mso${id}`;

          return (
            <div
              key={label}
              ref={(el) => {
                tabRefs.current[i] = el;
              }}
              className={[
                "capitalize w-full text-center whitespace-nowrap text-sm sm:text-base rounded-full transition",
                isActive
                  ? "bg-white text-primaryBlue font-bold"
                  : "text-[#202020A6] hover:text-primaryBlue hover:bg-[#ffffff8f]",
              ].join(" ")}
            >
              <Link
                key={label}
                aria-label={"Here Is the complete detail about " + label}
                href={href}
                prefetch={true}
                className="inline-block w-full h-full px-4 py-2 rounded-full"
              >
                {label}
              </Link>
            </div>
          );
        })}
      </div>
    </section>
  );
};
